import React from 'react'
import { ComponentConfig, ComponentData } from '@puckeditor/core'
import { DropdownButton } from 'steps/referral/parts/DropdownButton2'
import { referralButtonVars } from '../ReferralButton/ReferralButton'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import {
  TypographyProps,
  fontFamilyField,
  fontFamilyFallbackField,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'

export type ReferralDropdownItem = {
  label: string
  link: string
}

export type ReferralDropdownProps = {
  id?: string
  menuText: string
  buttonColor?: string
  buttonHoverColor?: string
  textColor?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  borderRadius?: string
  items: ReferralDropdownItem[]
}

/** DropdownButton2 style props derived from the puck fields — shared by the
 * editor render and the production mapping. */
export const referralDropdownStyles = (p: {
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  borderRadius?: string
}) => ({
  extraButtonStyle: applyTypography({
    fontFamily: p.fontFamily,
    fontFamilyFallback: p.fontFamilyFallback,
  }),
  groupStyle: p.borderRadius
    ? { borderRadius: p.borderRadius, overflow: 'hidden' as const }
    : undefined,
})

export const REFERRAL_DROPDOWN_DEFAULT_PROPS: ReferralDropdownProps = {
  menuText: 'Pick a Product',
  items: [
    { label: 'Product 1', link: 'https://' },
    { label: 'Product 2', link: 'https://' },
  ],
}

/** First ReferralDropdown in a card's action slot. */
export const getCardDropdown = (
  action?: ComponentData[],
): { type: string; props: ReferralDropdownProps } | undefined =>
  (
    action as { type: string; props: ReferralDropdownProps }[] | undefined
  )?.find(a => a.type === 'ReferralDropdown')

/**
 * MENU-type referral card action — legacy dropdown of product links
 * (DropdownButton2). In production the container maps this to the legacy
 * `link.menu` shape and ReferralCardNoVoting renders the dropdown itself,
 * with `_select` / `_select_click` analytics wired by ReferralStep3.
 */
export const PuckReferralDropdown: ComponentConfig<{
  props: ReferralDropdownProps
  fields: ThemeCustomFields
}> = {
  label: 'Referral Dropdown',
  defaultProps: REFERRAL_DROPDOWN_DEFAULT_PROPS,
  fields: {
    menuText: { type: 'text', label: 'Menu Text' },
    buttonColor: {
      type: 'colorPicker',
      label: 'Button Color',
      clearable: true,
    },
    buttonHoverColor: {
      type: 'colorPicker',
      label: 'Button Hover Color',
      clearable: true,
    },
    textColor: { type: 'colorPicker', label: 'Text Color', clearable: true },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    borderRadius: { type: 'text', label: 'Border Radius', placeholder: '4px' },
    items: {
      type: 'array',
      label: 'Menu Items',
      getItemSummary: (item: ReferralDropdownItem, i) =>
        item?.label || `Item ${(i ?? 0) + 1}`,
      arrayFields: {
        label: { type: 'text', label: 'Label' },
        link: { type: 'text', label: 'Link URL' },
      },
      defaultItemProps: { label: 'Product', link: 'https://' },
    },
  },
  // Editor-only render — production goes through the legacy MENU card.
  render: props => {
    const { menuText, buttonColor, buttonHoverColor, textColor, items } = props
    if ((items ?? []).length === 1) {
      return (
        <a
          className="shop-btn puck-shop-btn"
          href={items[0].link}
          onClick={e => e.preventDefault()}
          style={{
            color: textColor || '#fff',
            ...referralButtonVars({
              buttonColor,
              buttonHoverColor,
            }),
            ...referralDropdownStyles(props).extraButtonStyle,
            ...(props.borderRadius
              ? { borderRadius: props.borderRadius }
              : {}),
          }}
        >
          {items[0].label || 'Shop Now'}
        </a>
      )
    }
    return (
      <DropdownButton
        text={menuText || 'Pick a Product'}
        items={(items ?? []).map((item, i) => ({
          id: `menu-item-${i}`,
          label: item.label,
          link: item.link,
        }))}
        color={buttonColor || 'var(--theme-color-button, #4c3371)'}
        hoverColor={buttonHoverColor || undefined}
        textColor={textColor || undefined}
        order={0}
        {...referralDropdownStyles(props)}
      />
    )
  },
}
